import {
  Alert,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import moment, { now } from "moment";
import { LoadingButton } from "@mui/lab";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import PostHooks from "../../../Api/PostHooks";
import { Link, useNavigate } from "react-router-dom";
import AlertComponent from "../../../Components/AlertComponent";
import { Group } from "@mui/icons-material";
import ResidentialDropdowns from "../../../Components/ResidentialDropdowns";
import Page from "../../../Components/Page";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomSelect from "../../../Components/CustomSelect";

const CreateUserForm = () => {
  const methods = useForm();
  const {
    watch,
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = methods;
  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [userRole, setUserRole] = useState();
  const [channelName, setChannelName] = useState("mobile_number");

  const msisdn = watch("msisdn");

  const [loading, , request] = PostHooks();

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const channelOptions = [
    { value: "mobile_number", label: "Mobile Number" },
    { value: "email", label: "Email" },
  ];

  const roleOptions = [
    { value: "admin", label: "Admin" },
    { value: "finance", label: "Finance" },
    { value: "ccr", label: "CCR" },
  ];

  const onSubmit = async (e) => {
    try {
      if (channelName === "mobile_number" && msisdn && msisdn.length < 13) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Input a valid phone number!",
        });
      } else {
        let formData = {
          first_name: e.first_name,
          surname: e.sirname_name,
          other_names: e.other_names,
          name: e.first_name + " " + e.sirname_name + " " + e.other_names,
          company_name: "Digital Sacco",
          state: "county",
          residential_address: "residential_address",
          postal_address: "postal_address",
          id_number: e.identification_number,
          passport_number: e.identification_number,
          county: "county",
          subcounty: "subcounty",
          ward: "ward",
          village: "village",
          serial_no: "",
          msisdn:
            channelName === "mobile_number" ? msisdn.replace("+", "") : e.email,
          document_registration_date: moment(now).format("YYYY-MM-DD"),
          document_expiry_date: "",
          document_number: e.identification_number,
          description: "Portal user creation",
          registry_file_number: e.identification_number,
          role: userRole,
          channel_name: channelName,
          username: e.identification_number,
          next_kin_edit: "",
          next_kin_id: "",
          member_entity_id: "",
          allocation: "",
          kyc_id: "",
          otp_msisdn:
            channelName === "mobile_number"
              ? msisdn.replace("+", "")
              : e.otp_msisdn.replace("+", ""),
        };

        let props = {
          url: "/user",
          body: formData,
        };

        const response = await request(props);

        if (
          response &&
          response.status === 200 &&
          response.data.StatusCode === 0
        ) {
          setSnackbar({
            open: true,
            severity: "success",
            message: "Registration successful",
          });

          setTimeout(() => {
            navigate(-1);
          }, 500);
        } else {
          const errorMessage =
            response?.data?.StatusMessage ??
            response?.data?.message ??
            "An unexpected error occurred. Please try again.";

          setSnackbar({
            open: true,
            severity: "error",
            message: errorMessage,
          });
        }
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.StatusMessage ??
        error.response?.data?.message ??
        error.message ??
        "An unexpected error occurred. Please try again.";

      setSnackbar({
        open: true,
        severity: "error",
        message: errorMessage,
      });
    }
  };

  return (
    <Page
      title={"Create Portal User"}
      actions={
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      }
    >
      <AlertComponent
        open={snackbar.open}
        handleSnackbar={handleSnackbarClose}
        severity={{ severity: snackbar.severity, message: snackbar.message }}
      />

      <Card
        sx={{
          p: 2,
        }}
      >
        <CardContent>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} id="cust_reg_form">
              <Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 2 }}>
                <Grid
                  item
                  xs={6}
                  md={4}
                  lg={4}
                  sx={{
                    paddingTop: "20px",
                  }}
                >
                  <FormControl fullWidth error={!!errors.first_name}>
                    <InputLabel htmlFor="first_name">First name</InputLabel>
                    <OutlinedInput
                      id="first_name"
                      {...register("first_name", { required: true })}
                      label="First name"
                      xs={12}
                      variant="outlined"
                    />
                    {errors.first_name && (
                      <FormHelperText>Required</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={6}
                  md={4}
                  lg={4}
                  sx={{
                    paddingTop: "20px",
                  }}
                >
                  <FormControl fullWidth error={!!errors.sirname_name}>
                    <InputLabel htmlFor="sirname_name">Sir name</InputLabel>
                    <OutlinedInput
                      id="sirname_name"
                      {...register("sirname_name", { required: true })}
                      label="Sir name"
                      xs={12}
                      variant="filled"
                    />
                    {errors.sirname_name && (
                      <FormHelperText>Required</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={4}
                  lg={4}
                  sx={{
                    paddingTop: "20px",
                  }}
                >
                  <FormControl fullWidth error={!!errors.other_names}>
                    <InputLabel htmlFor="other_names">Other name</InputLabel>
                    <OutlinedInput
                      variant="filled"
                      id="other_names"
                      {...register("other_names", { required: true })}
                      label="Other name"
                      xs={12}
                    />
                    {errors.other_names && (
                      <FormHelperText>Required</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    paddingTop: "20px",
                  }}
                >
                  <CustomSelect
                    labelId="user_role"
                    id="user_role"
                    label="User role"
                    options={roleOptions}
                    value={userRole}
                    onChange={(e) => setUserRole(e.target.value)}
                    required
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    paddingTop: "20px",
                  }}
                >
                  <CustomSelect
                    labelId="channel"
                    id="channel"
                    label="Login Channel"
                    options={channelOptions}
                    value={channelName}
                    onChange={(e) => setChannelName(e.target.value)}
                    required
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    paddingTop: "20px",
                  }}
                >
                  <FormControl fullWidth error={!!errors.identification_number}>
                    <InputLabel>National ID Number/ Passport Number</InputLabel>
                    <OutlinedInput
                      variant="filled"
                      id="identification_number"
                      {...register("identification_number", {
                        required: true,
                        maxLength: 10,
                        minLength: 7,
                      })}
                      label="National ID Number/ Passport Number"
                      xs={12}
                      type="number"
                      inputProps={{ step: "any", min: 0 }}
                    />
                    {errors.identification_number && (
                      <FormHelperText>Enter a valid id number.</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* 
        <Grid
          item
          xs={12}
          md={12}
          lg={12}
          sx={{
            paddingTop: "20px",
          }}
        >
          <FormControl fullWidth error={!!errors.username}>
            <InputLabel htmlFor="username">Username</InputLabel>
            <OutlinedInput
              variant="filled"
              id="username"
              {...register("username", { required: true })}
              label="Username"
              xs={12}
            />
            {errors.username && <FormHelperText>Required</FormHelperText>}
          </FormControl>
        </Grid> */}

                {channelName === "mobile_number" ? (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      paddingTop: "20px",
                    }}
                  >
                    <Controller
                      name="msisdn"
                      control={control}
                      rules={{
                        validate: (value) => matchIsValidTel(value),
                      }}
                      defaultValue={"+254"}
                      render={({ field, fieldState }) => (
                        <MuiTelInput
                          {...field}
                          fullWidth
                          defaultCountry="KE"
                          onlyCountries={["KE"]}
                          helperText={
                            fieldState.invalid ? "Invalid mobile number!" : ""
                          }
                          error={fieldState.error}
                          value={field.value || ""}
                          disableFormatting
                        />
                      )}
                    />
                  </Grid>
                ) : (
                  channelName === "email" && (
                    <>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                          paddingTop: "20px",
                        }}
                      >
                        <FormControl fullWidth error={!!errors.email}>
                          <InputLabel htmlFor="email">Email</InputLabel>
                          <OutlinedInput
                            variant="filled"
                            id="email"
                            {...register("email", {
                              required: true,
                            })}
                            label="Email"
                            xs={12}
                            type="email"
                          />
                          {errors.email && (
                            <FormHelperText>Required</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                          paddingTop: "20px",
                        }}
                      >
                        <Controller
                          name="otp_msisdn"
                          control={control}
                          rules={{
                            validate: (value) => matchIsValidTel(value),
                          }}
                          defaultValue={"+254"}
                          render={({ field, fieldState }) => (
                            <MuiTelInput
                              {...field}
                              fullWidth
                              defaultCountry="KE"
                              onlyCountries={["KE"]}
                              helperText={
                                fieldState.invalid
                                  ? "Invalid mobile number!"
                                  : ""
                              }
                              error={fieldState.error}
                              value={field.value || ""}
                              disableFormatting
                              required={channelName === "email"}
                            />
                          )}
                        />
                        <FormHelperText>
                          Enter a phone number that will receive the OTP
                        </FormHelperText>
                      </Grid>
                    </>
                  )
                )}

                <Grid
                  container
                  display={"flex"}
                  justifyContent={"end"}
                  spacing={4}
                  sx={{
                    py: "20px",
                  }}
                >
                  <Grid item>
                    <Button
                      variant="outlined"
                      type="reset"
                      size="large"
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <LoadingButton
                      loading={loading}
                      variant="contained"
                      type="submit"
                      size="large"
                    >
                      Create
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </CardContent>
      </Card>
    </Page>
  );
};

export default CreateUserForm;
